import React, { useEffect, useState } from 'react'
import Dialog from '../../../../common/element/Dialog'
import Tabs from '../../../../common/element/Tabs/Tabs'
import TabList from '../../../../common/element/Tabs/TabList'
import TabNav from '../../../../common/element/Tabs/TabNav'
import TabContent from '../../../../common/element/Tabs/TabContent'
import BtnPrimary from '../../../../common/element/Buttons/btnPrimary'
import Select from '../../../../common/element/Select'
import Upload from '../../../../common/element/Upload'
import CommonPhoneInput from '../../../../common/CommonPhoneInput/CommonPhoneInput'
import ThreeDotLoader from "../../../../common/element/ButtonLoader/ThreeDotLoader"
import SweetAlertService from '../../../../common/SweetAlert/SweetAlert'
import { clearCities, clearStates, getCityList, getCountryList, getStateList } from '../../../../redux/countryStateCity/cityStateCountrySlice'
import { selectCityList, selectCountryList, selectStateList } from '../../../../redux/countryStateCity/cityStateCountrySelector'
import { useDispatch, useSelector } from 'react-redux'
import { createCompanyProspect, generateCompanyProspectUrl, clearGenerateCompanyProspectUrl, updateCompanyProspect } from '../../../../redux/ProspectCompany/companyProspectSlice'
import { selectGenerateCompanyProspectUrlData, selectGenerateCompanyProspectUrlLoading } from '../../../../redux/ProspectCompany/companyProspectSelectors'
import { getSessionMasterList } from '../../../../redux/SessionMaster/sessionMasterSlice'
import { selectSessionMasterList } from '../../../../redux/SessionMaster/sessionMasterSelector'

// ── Defined at module level so it's always stable (no re-creation on each render) ──
const numberOfEmployeesOptions = [
    { value: "1-10", label: "1-10" },
    { value: "11-50", label: "11-50" },
    { value: "51-200", label: "51-200" },
    { value: "201-500", label: "201-500" },
    { value: "500+", label: "500+" }
];

// Matches a saved value (string range OR raw number) to the correct dropdown option
const resolveEmployeesOption = (saved) => {
    if (!saved) return "";
    // Exact string match — e.g. "51-200" saved as-is
    const exact = numberOfEmployeesOptions.find(o => o.value === saved);
    if (exact) return exact;
    // Numeric value — find which range bucket it falls into (e.g. API returns 200)
    const num = Number(saved);
    if (!isNaN(num)) {
        if (num <= 10) return numberOfEmployeesOptions[0]; // 1-10
        if (num <= 50) return numberOfEmployeesOptions[1]; // 11-50
        if (num <= 200) return numberOfEmployeesOptions[2]; // 51-200
        if (num <= 500) return numberOfEmployeesOptions[3]; // 201-500
        return numberOfEmployeesOptions[4];                 // 500+
    }
    // Fallback: show the raw value as-is
    return { value: saved, label: saved };
};

const AddEditCompany = ({ dialogOpen, onDialogClose, companyData, isEdit }) => {
    const [tab, setTab] = useState("tab1");
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const countryList = useSelector(selectCountryList);
    const stateList = useSelector(selectStateList);
    const cityList = useSelector(selectCityList);
    const isGeneratingUrl = useSelector(selectGenerateCompanyProspectUrlLoading);
    const generatedUrlData = useSelector(selectGenerateCompanyProspectUrlData);
    const sessionList = useSelector(selectSessionMasterList);

    const dispatch = useDispatch();
    const [disablePrevTabs, setDisablePrevTabs] = useState(false);

    const countryOptions = countryList.map(country => ({
        value: country.country_id,
        label: country.country_name
    }));

    const initialFormData = {
        company_name: "",
        contact_person: "",
        contact_email: "",
        work_phone: "",
        contact_phone: "",
        first_name: "",
        last_name: "",
        email: "",
        mobile_number: "",
        max_employees: "",

        address: "",
        addressLine2: "",
        city: 0,
        state: 0,
        pincode: "",
        phone_landline: "",
        phone_landline_country: null,
        country: 0,

        // Other Details (Tab 3)
        notesAndDocuments: [{ notes: "", documents: [] }],
        social_links: [{ url: "" }],
        session_id: "",   // mandatory

        // IDs
        company_id: 0,

        // Generate URL (Tab 4)
        generateUrl: ""
    };

    const [formData, setFormData] = useState(initialFormData);

    const initialFormErrors = {
        company_name: "",
        contact_person: "",
        contact_email: "",
        work_phone: "",
        contact_phone: "",
        first_name: "",
        last_name: "",
        email: "",
        mobile_number: "",
        max_employees: "",
        address: "",
        addressLine2: "",
        city: "",
        state: "",
        pincode: "",
        phone_landline: "",
        country: "",
        notes: "",
        documents: "",
        social_links: "",
        session_id: "",
        generateUrl: ""
    };

    const [formError, setFormErrors] = useState(initialFormErrors);
    const [copyMsg, setCopyMsg] = useState({ text: '', type: '' });

    const sessionOptions = sessionList.map(session => ({
        value: session.session_id,
        label: session.session_name || ""
    }));

    const stateOptions = stateList.map(state => ({
        value: state.state_id,
        label: state.state_name
    }));

    const cityOptions = cityList.map(city => ({
        value: city.city_id,
        label: city.city_name
    }));

    useEffect(() => {
        if (isEdit && companyData) {
            // ── FIX: fetch state and city lists so dropdowns are populated in edit mode ──
            const countryId = companyData.country?.country_id || companyData.country || 0;
            const stateId = companyData.state?.state_id || companyData.state || 0;
            if (countryId) dispatch(getStateList(countryId));
            if (stateId) dispatch(getCityList(stateId));

            setFormData({
                company_name: companyData.company_name || "",
                contact_person: companyData.contact_person || "",
                contact_email: companyData.contact_email || "",
                work_phone: companyData.work_phone || "",
                contact_phone: companyData.contact_phone || "",
                first_name: companyData.hr?.first_name || "",
                last_name: companyData.hr?.last_name || "",
                email: companyData.hr?.email || "",
                mobile_number: companyData.hr?.mobile_number || "",
                // ── FIX: smart range-based matching — handles both "51-200" and raw 200 ──
                max_employees: resolveEmployeesOption(companyData.max_employees),
                address: companyData.address?.split('/n')[0] || "",
                addressLine2: companyData.address?.split('/n').slice(1).join('/n') || "",
                city: companyData.city?.city_id || companyData.city || 0,
                state: companyData.state?.state_id || companyData.state || 0,
                pincode: companyData.pincode || "",
                phone_landline: companyData.phone_landline || "",
                country: companyData.country?.country_id || companyData.country || 0,
                notesAndDocuments: (companyData.notesAndDocuments && companyData.notesAndDocuments.length > 0)
                    ? companyData.notesAndDocuments
                    : [{ notes: "", documents: [] }],
                social_links: (companyData.social_links && companyData.social_links.length > 0)
                    ? companyData.social_links.map(url => ({ url }))
                    : [{ url: "" }],
                session_id: sessionOptions.find(opt => Number(opt.value) === Number(companyData.session_id)) || "",
                company_id: companyData.company_id || 0,
                generateUrl: companyData.generateUrl || ""
            });
        } else {
            setFormData(initialFormData);
        }
    }, [isEdit, companyData, dialogOpen, sessionList]);

    useEffect(() => {
        if (generatedUrlData) {
            const newUrl = generatedUrlData.generatedUrl || generatedUrlData.url || (typeof generatedUrlData === 'string' ? generatedUrlData : "");
            const newId = generatedUrlData.company_id;
            setFormData(prev => ({
                ...prev,
                generateUrl: newUrl,
                company_id: newId || prev.company_id
            }));
        }
    }, [generatedUrlData]);

    const handlePhoneKeyPress = (e) => {
        const charCode = e.which ? e.which : e.keyCode;
        if (charCode > 31 && (charCode < 48 || charCode > 57)) {
            e.preventDefault();
        }
    };

    const handleCountryChange = (value) => {
        handleInputChange("country", value);
        handleInputChange("state", 0);
        handleInputChange("city", 0);
        dispatch(clearCities());
        if (value) {
            dispatch(getStateList(value));
        } else {
            dispatch(clearStates());
        }
    };

    const handleStateChange = (value) => {
        handleInputChange("state", value);
        handleInputChange("city", 0);
        if (value) {
            dispatch(getCityList(value));
        } else {
            dispatch(clearCities());
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (formError[field]) {
            setFormErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    const handleSocialMediaChange = (index, value) => {
        const updatedLinks = [...formData.social_links];
        updatedLinks[index] = { ...updatedLinks[index], url: value };
        setFormData(prev => ({ ...prev, social_links: updatedLinks }));
    };

    const addSocialMediaField = () => {
        setFormData(prev => ({
            ...prev,
            social_links: [...prev.social_links, { url: "" }]
        }));
    };

    const removeSocialMediaField = (index) => {
        if (formData.social_links.length > 1) {
            const updatedLinks = formData.social_links.filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, social_links: updatedLinks }));
        }
    };

    const handleNotesChange = (index, value) => {
        const updatedNotesAndDocs = [...formData.notesAndDocuments];
        updatedNotesAndDocs[index] = { ...updatedNotesAndDocs[index], notes: value };
        setFormData(prev => ({ ...prev, notesAndDocuments: updatedNotesAndDocs }));
        if (formError[`notes_${index}`]) {
            setFormErrors(prev => ({ ...prev, [`notes_${index}`]: "" }));
        }
    };

    const handleNotesDocumentsChange = (index, files) => {
        const updatedNotesAndDocs = [...formData.notesAndDocuments];
        updatedNotesAndDocs[index] = { ...updatedNotesAndDocs[index], documents: files };
        setFormData(prev => ({ ...prev, notesAndDocuments: updatedNotesAndDocs }));
    };

    const addNotesAndDocumentsField = () => {
        setFormData(prev => ({
            ...prev,
            notesAndDocuments: [...prev.notesAndDocuments, { notes: "", documents: [] }]
        }));
    };

    const removeNotesAndDocumentsField = (index) => {
        if (formData.notesAndDocuments.length > 1) {
            const updatedNotesAndDocs = formData.notesAndDocuments.filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, notesAndDocuments: updatedNotesAndDocs }));
        }
    };

    const removeFileFromNotesDocuments = (notesIndex, fileIndex) => {
        const updatedNotesAndDocs = [...formData.notesAndDocuments];
        updatedNotesAndDocs[notesIndex] = {
            ...updatedNotesAndDocs[notesIndex],
            documents: updatedNotesAndDocs[notesIndex].documents.filter((_, i) => i !== fileIndex)
        };
        setFormData(prev => ({ ...prev, notesAndDocuments: updatedNotesAndDocs }));
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Tab 1 validation — mandatory fields
    const validateTab1 = () => {
        const errors = {};
        if (!formData.company_name) errors.company_name = "Company name is required";
        if (!formData.contact_email) {
            errors.contact_email = "Email address is required";
        } else if (!validateEmail(formData.contact_email)) {
            errors.contact_email = "Please enter a valid email address";
        }

        // HR Information Validation (First Name & Email Required, Last Name Optional)
        if (!formData.first_name) errors.first_name = "HR first name is required";
        
        // Removed last_name validation as requested
        // if (!formData.last_name) errors.last_name = "HR last name is required"; 

        if (!formData.email) {
            errors.email = "HR email is required";
        } else if (!validateEmail(formData.email)) {
            errors.email = "Please enter a valid email address";
        }
        if (!formData.mobile_number) {
            errors.mobile_number = "HR mobile number is required";
        }

        if (!formData.max_employees) errors.max_employees = "Number of employees is required";

        const phoneFields = ['work_phone', 'contact_phone', 'mobile_number'];
        phoneFields.forEach(field => {
            const country = formData[`${field}_country`];
            // Only validate phone length if value is present, except for mobile_number which is required above anyway
            if (formData[field] && !isPhoneValid(formData[field], country)) {
                errors[field] = 'Invalid phone number';
            }
        });

        setFormErrors(prev => ({ ...prev, ...errors }));
        return Object.keys(errors).length === 0;
    };

    // Tab 2 validation — ALL fields non-mandatory, always passes
    const validateTab2 = () => {
        const errors = {};
        if (!isPhoneValid(formData.phone_landline, formData.phone_landline_country)) {
            errors.phone_landline = 'Invalid phone number';
        }
        setFormErrors(prev => ({ ...prev, ...errors }));
        return Object.keys(errors).length === 0;
    };

    // Tab 3 validation — session_id is mandatory
    const validateTab3 = () => {
        const errors = {};
        if (!formData.session_id) {
            errors.session_id = "Assign Session is required";
        }

        // Notes are mandatory if a document is uploaded
        formData.notesAndDocuments.forEach((item, index) => {
            if (item.documents && item.documents.length > 0 && (!item.notes || item.notes.trim() === "")) {
                errors[`notes_${index}`] = "Notes are required when a document is uploaded";
            }
        });

        setFormErrors(prev => ({ ...prev, ...errors }));
        return Object.keys(errors).length === 0;
    };

    const isPhoneValid = (value, country) => {
        if (!value) return true;
        if (country && country.format) {
            const requiredLength = (country.format.match(/\./g) || []).length;
            const digitsOnly = String(value).replace(/\D/g, '');
            return digitsOnly.length >= requiredLength;
        }
        return true;
    };

    const handlePhoneChange = (field, value, country) => {
        setFormData(prev => ({ ...prev, [field]: value, [`${field}_country`]: country }));

        if (!value) {
            setFormErrors(prev => ({ ...prev, [field]: "" }));
            return;
        }

        if (country && country.format) {
            const requiredLength = (country.format.match(/\./g) || []).length;
            const digitsOnly = String(value).replace(/\D/g, '');
            if (digitsOnly.length < requiredLength) {
                setFormErrors(prev => ({ ...prev, [field]: "Invalid phone number" }));
            } else {
                setFormErrors(prev => ({ ...prev, [field]: "" }));
            }
        } else {
            setFormErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    // Shared helper to build FormData payload (used in both handleNext and handleSubmit)
    const buildFormData = () => {
        const fd = new FormData();
        fd.append("company_name", formData.company_name || "");
        fd.append("contact_person", formData.contact_person || "");
        fd.append("contact_email", formData.contact_email || "");
        fd.append("contact_phone", formData.contact_phone || "");
        fd.append("work_phone", formData.work_phone || "");
        fd.append("first_name", formData.first_name || "");
        fd.append("last_name", formData.last_name || "");
        fd.append("mobile_number", formData.mobile_number || "");
        fd.append("max_employees", formData.max_employees?.value || "");
        fd.append("address", `${formData.address}${formData.addressLine2 ? '/n' + formData.addressLine2 : ''}`.trim());
        fd.append("pincode", formData.pincode || "");
        fd.append("phone_landline", formData.phone_landline || "");
        fd.append("city", formData.city || 0);
        fd.append("state", formData.state || 0);
        fd.append("country", formData.country || 0);
        // ── Only include session_id when adding, not editing ──
        if (!isEdit) {
            fd.append("session_id", formData.session_id?.value || 0);
        }
        fd.append("email", formData.email || "");

        const social = formData.social_links.map(link => link.url).filter(u => u.trim() !== "");
        social.forEach((s, i) => fd.append(`social_links[${i}]`, s));

        const filteredNotes = formData.notesAndDocuments.filter(
            item => (item.notes && item.notes.trim() !== "") || (item.documents && item.documents.length > 0)
        );
        filteredNotes.forEach((item, idx) => {
            fd.append(`notesAndDocuments[${idx}][notes]`, item.notes || "");
            if (item.documents && item.documents.length > 0) {
                item.documents.forEach(file => {
                    if (file instanceof File) fd.append(`notesAndDocuments[${idx}][documents]`, file);
                    else if (file.url) fd.append(`notesAndDocuments[${idx}][documents]`, file.url);
                    else if (typeof file === 'string') fd.append(`notesAndDocuments[${idx}][documents]`, file);
                });
            }
        });

        return fd;
    };

    const handleNext = () => {
        if (tab === "tab1" && validateTab1()) {
            setTab("tab2");
        } else if (tab === "tab2" && validateTab2()) {
            setTab("tab3");
        } else if (tab === "tab3" && validateTab3()) {
            if (isEdit) {
                // Edit mode: call update API and close dialog on success
                const fd = buildFormData();
                // Ensure company_id is sent in body as required by backend
                if (formData.company_id) fd.append("company_id", formData.company_id);
                setButtonDisabled(true);
                dispatch(updateCompanyProspect({ companyId: formData.company_id, prospectData: fd })).then((action) => {
                    setButtonDisabled(false);
                    if (action.meta.requestStatus === "fulfilled") {
                        SweetAlertService.showSuccess({ message: 'Company updated successfully' });
                        handleDialogClose(true);
                    } else {
                        const errMsg = action?.payload;
                        SweetAlertService.showError({ icon: 'error', message: errMsg });
                    }
                });
            } else {
                // Add mode: generate URL via API then go to tab4
                const fd = buildFormData();
                dispatch(generateCompanyProspectUrl(fd)).then((action) => {
                    if (action.meta.requestStatus === "fulfilled") {
                        const data = action.payload?.data || action.payload;
                        setFormData(prev => ({
                            ...prev,
                            generateUrl: data.generatedUrl || data.url || (typeof data === 'string' ? data : ""),
                            company_id: data.company_id || prev.company_id
                        }));
                        setDisablePrevTabs(true);
                        SweetAlertService.showToast({ title: 'Company URL generated successfully' });
                        setTab("tab4");
                    } else {
                        const errMsg = action?.payload;
                        SweetAlertService.showError({ icon: 'error', message: errMsg });
                    }
                });
            }
        }
    };

    const handleTabClick = (targetTab) => {
        const order = ['tab1', 'tab2', 'tab3', 'tab4'];
        const currentIndex = order.indexOf(tab);
        const targetIndex = order.indexOf(targetTab);

        if (targetIndex === currentIndex) return;

        if (targetIndex < currentIndex) {
            if (disablePrevTabs) return;
            setTab(targetTab);
            return;
        }

        if (targetIndex > currentIndex + 1) return;

        if (tab === "tab3" && targetTab === "tab4") {
            handleNext();
            return;
        }

        if (tab === "tab1" && !validateTab1()) return;
        if (tab === "tab2" && !validateTab2()) return;

        setTab(targetTab);
    };

    const handleDialogClose = (refresh = false) => {
        setFormData(initialFormData);
        setFormErrors(initialFormErrors);
        setTab("tab1");
        setDisablePrevTabs(false);
        setButtonDisabled(false);
        dispatch(clearGenerateCompanyProspectUrl());
        onDialogClose(refresh);
    };

    const copyUrlToClipboard = async () => {
        const url = formData.generateUrl;
        if (!url) {
            setCopyMsg({ text: 'No URL to copy', type: 'error' });
            setTimeout(() => setCopyMsg({ text: '', type: '' }), 3000);
            return;
        }
        try {
            await navigator.clipboard.writeText(url);
            setCopyMsg({ text: 'Copied to clipboard!', type: 'success' });
            setTimeout(() => setCopyMsg({ text: '', type: '' }), 3000);
        } catch (err) {
            setCopyMsg({ text: 'Unable to copy URL', type: 'error' });
            setTimeout(() => setCopyMsg({ text: '', type: '' }), 3000);
        }
    };

    useEffect(() => {
        dispatch(getCountryList());
        dispatch(getSessionMasterList({ access_group_id: 5, archived: false }));
    }, [dispatch]);

    const DeleteIcon = () => (
        <svg width="17" height="19" viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.6673 4.16671V3.50004C11.6673 2.56662 11.6673 2.09991 11.4857 1.74339C11.3259 1.42979 11.0709 1.17482 10.7573 1.01503C10.4008 0.833374 9.93407 0.833374 9.00065 0.833374H7.66732C6.7339 0.833374 6.26719 0.833374 5.91067 1.01503C5.59706 1.17482 5.3421 1.42979 5.18231 1.74339C5.00065 2.09991 5.00065 2.56662 5.00065 3.50004V4.16671M6.66732 8.75004V12.9167M10.0007 8.75004V12.9167M0.833984 4.16671H15.834M14.1673 4.16671V13.5C14.1673 14.9002 14.1673 15.6002 13.8948 16.135C13.6552 16.6054 13.2727 16.9879 12.8023 17.2276C12.2675 17.5 11.5674 17.5 10.1673 17.5H6.50065C5.10052 17.5 4.40045 17.5 3.86567 17.2276C3.39527 16.9879 3.01282 16.6054 2.77313 16.135C2.50065 15.6002 2.50065 14.9002 2.50065 13.5V4.16671" stroke="white" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const CrossIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    return (
        <Dialog
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            width={900}
            onRequestClose={handleDialogClose}
            bodyOpenClassName="overflow-hidden"
        >
            {/* Header */}
            <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center justify-between">
                <div className="flex items-center">
                    <span className="h-12 w-12 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
                        <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9.3291 2.88062C9.09958 2.84234 8.86414 2.90677 8.68652 3.05737C8.50886 3.20785 8.40625 3.42895 8.40625 3.66187V17.5076C8.40625 17.9447 8.7618 18.2996 9.19922 18.2996C9.63653 18.2993 9.99121 17.9446 9.99121 17.5076V4.59644L16.7148 5.71655V16.7156H11.9678C11.5305 16.7158 11.1758 17.0705 11.1758 17.5076C11.1758 17.9446 11.5305 18.2994 11.9678 18.2996H17.5068C17.9446 18.2996 18.2988 17.9451 18.2988 17.5076V5.04663C18.2988 4.65944 18.0188 4.3282 17.6367 4.2644L9.3291 2.88062Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M11.9678 13.254C11.5301 13.2541 11.1758 13.6084 11.1758 14.046V17.507C11.1758 17.9446 11.5301 18.2999 11.9678 18.2999H14.7373C15.1752 18.2999 15.5293 17.9446 15.5293 17.507V14.046C15.5293 13.6083 15.1751 13.254 14.7373 13.254H11.9678ZM13.9453 16.715H12.7607V14.838H13.9453V16.715Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M3.65918 13.254C3.2216 13.2542 2.86719 13.6084 2.86719 14.046V17.507C2.86719 17.9446 3.22158 18.2998 3.65918 18.2999H6.42871C6.86645 18.2999 7.2207 17.9446 7.2207 17.507V14.046C7.2207 13.6083 6.86643 13.254 6.42871 13.254H3.65918ZM5.63672 16.715H4.45215V14.838H5.63672V16.715Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M1.02246 0.111084C0.793089 0.0730985 0.557591 0.136899 0.379883 0.287842C0.20242 0.438363 0.0996094 0.659418 0.0996094 0.892334V17.5076C0.0997506 17.9445 0.455149 18.2996 0.892578 18.2996C1.32979 18.2994 1.68443 17.9444 1.68457 17.5076V1.8269L8.4082 2.94702V16.7146H3.66113C3.22389 16.7148 2.86914 17.0706 2.86914 17.5076C2.86932 17.9444 3.224 18.2994 3.66113 18.2996H9.2002C9.63794 18.2996 9.99201 17.945 9.99219 17.5076V2.2771C9.99219 1.88969 9.71208 1.55952 9.33008 1.49585L1.02246 0.111084Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M14.3906 9.79297C14.1447 9.79323 13.9456 9.99233 13.9453 10.2383V10.9307C13.9453 11.1767 14.1446 11.3767 14.3906 11.377H15.083C15.3294 11.377 15.5293 11.1769 15.5293 10.9307V10.2383C15.529 9.99213 15.3292 9.79297 15.083 9.79297H14.3906Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M11.624 9.79297C11.3778 9.79297 11.178 9.99215 11.1777 10.2383V10.9307C11.1777 11.1769 11.3777 11.377 11.624 11.377H12.3154C12.5618 11.377 12.7617 11.1769 12.7617 10.9307V10.2383C12.7615 9.99213 12.5616 9.79297 12.3154 9.79297H11.624Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M11.624 7.02332C11.3778 7.02332 11.178 7.22263 11.1777 7.46863V8.16101C11.1777 8.40724 11.3777 8.6073 11.624 8.6073H12.3154C12.5618 8.6073 12.7617 8.40722 12.7617 8.16101V7.46863C12.7615 7.22262 12.5616 7.02332 12.3154 7.02332H11.624Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M14.3906 7.02332C14.1447 7.02358 13.9456 7.22282 13.9453 7.46863V8.16101C13.9453 8.40706 14.1446 8.60704 14.3906 8.6073H15.083C15.3294 8.6073 15.5293 8.40722 15.5293 8.16101V7.46863C15.529 7.22262 15.3292 7.02332 15.083 7.02332H14.3906Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M6.08301 9.79297C5.8368 9.79297 5.63697 9.99215 5.63672 10.2383V10.9307C5.63672 11.1769 5.83664 11.377 6.08301 11.377H6.77539C7.02151 11.3767 7.2207 11.1767 7.2207 10.9307V10.2383C7.22045 9.99228 7.02132 9.79317 6.77539 9.79297H6.08301Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M3.3125 9.79297C3.06651 9.79323 2.86744 9.99231 2.86719 10.2383V10.9307C2.86719 11.1767 3.06635 11.3767 3.3125 11.377H4.00488C4.25113 11.377 4.45117 11.1769 4.45117 10.9307V10.2383C4.45092 9.99211 4.25094 9.79297 4.00488 9.79297H3.3125Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M3.3125 7.02332C3.06649 7.02358 2.86744 7.22279 2.86719 7.46863V8.16101C2.86719 8.40708 3.06635 8.60704 3.3125 8.6073H4.00488C4.25113 8.6073 4.45117 8.40725 4.45117 8.16101V7.46863C4.45092 7.22259 4.25096 7.02332 4.00488 7.02332H3.3125Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M6.08301 7.02332C5.83678 7.02332 5.63697 7.22263 5.63672 7.46863V8.16101C5.63672 8.40724 5.83664 8.6073 6.08301 8.6073H6.77539C7.02151 8.60709 7.2207 8.40708 7.2207 8.16101V7.46863C7.22045 7.22277 7.02134 7.02352 6.77539 7.02332H6.08301Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M6.08301 4.25378C5.83678 4.25378 5.63697 4.4531 5.63672 4.6991V5.39148C5.63672 5.63785 5.83666 5.83777 6.08301 5.83777H6.77539C7.02149 5.83756 7.2207 5.63768 7.2207 5.39148V4.6991C7.22045 4.45323 7.02134 4.25399 6.77539 4.25378H6.08301Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                            <path d="M3.3125 4.25378C3.06649 4.25405 2.86744 4.45326 2.86719 4.6991V5.39148C2.86719 5.63768 3.06637 5.83751 3.3125 5.83777H4.00488C4.25111 5.83777 4.45117 5.63785 4.45117 5.39148V4.6991C4.45092 4.45306 4.25096 4.25378 4.00488 4.25378H3.3125Z" fill="#344054" stroke="white" strokeWidth="0.2" />
                        </svg>
                    </span>
                    <div>
                        <h5 className="mb-0 font-lato not-italic font-medium text-[16px] leading-[150%] text-[#101828]">
                            {isEdit ? "Edit Company" : "Add Company"}
                        </h5>
                        <p className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#475467] mb-0">
                            {isEdit ? "Update company details" : "Create a new Company"}
                        </p>
                    </div>
                </div>
                <button onClick={handleDialogClose} className="text-gray-400 hover:text-gray-600">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>

            <Tabs value={tab} variant="pill">
                {/* Tab Navigation */}
                <div className="px-6 mb-5">
                    <div className="p-1 rounded-lg client-portal-tab" style={{ backgroundColor: '#FFF4E5' }}>
                        <TabList>
                            {(isEdit ? ["tab1", "tab2", "tab3"] : ["tab1", "tab2", "tab3", "tab4"]).map((t, i) => (
                                <TabNav
                                    key={t}
                                    value={t}
                                    onClick={() => handleTabClick(t)}
                                    style={{
                                        backgroundColor: tab === t ? '#ffffff' : 'transparent',
                                        color: tab === t ? '#344054' : '#667085',
                                        fontWeight: tab === t ? '600' : '500'
                                    }}
                                >
                                    {["Basic Details", "Address Details", "Other Details", "Generate URL"][i]}
                                </TabNav>
                            ))}
                        </TabList>
                    </div>
                </div>

                {/* ── Tab 1: Basic Details ── */}
                <TabContent value="tab1">
                    <div className="px-6">
                        {/* Company Info */}
                        <div className="flex mb-5">
                            <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                                Company Info*
                            </label>
                            <div className="w-3/4 pl-2 flex gap-3">
                                <div className="flex-1">
                                    <input
                                        className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={formData.company_name}
                                        placeholder="Enter Company name here"
                                        onChange={(e) => handleInputChange("company_name", e.target.value)}
                                    />
                                    {formError.company_name && (
                                        <p className="text-xs text-red-600 mt-1">{formError.company_name}</p>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <input
                                        className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={formData.contact_person}
                                        placeholder="Enter Contact person name here"
                                        onChange={(e) => handleInputChange("contact_person", e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Email */}
                        <div className="flex mb-5">
                            <label className="w-1/4" />
                            <div className="w-3/4 pl-2">
                                <input
                                    type="email"
                                    className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    value={formData.contact_email}
                                    placeholder="Enter Email address here"
                                    onChange={(e) => handleInputChange("contact_email", e.target.value)}
                                />
                                {formError.contact_email && (
                                    <p className="text-xs text-red-600 mt-1">{formError.contact_email}</p>
                                )}
                            </div>
                        </div>

                        {/* Work Phone / Phone Number */}
                        <div className="flex mb-5">
                            <label className="w-1/4" />
                            <div className="w-3/4 pl-2 flex gap-3">
                                <div className="flex-1">
                                    <CommonPhoneInput
                                        value={formData.work_phone}
                                        onChange={(phone, country) => handlePhoneChange('work_phone', phone, country)}
                                        onBlur={() => { }}
                                        placeholder="Enter Work phone number here"
                                        error={formError.work_phone}
                                    />
                                </div>
                                <div className="flex-1">
                                    <CommonPhoneInput
                                        value={formData.contact_phone}
                                        onChange={(phone, country) => handlePhoneChange('contact_phone', phone, country)}
                                        onBlur={() => { }}
                                        placeholder="Enter Phone number here"
                                        error={formError.contact_phone}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* HR Info */}
                        <div className="flex mb-5">
                            <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                                HR Info*
                            </label>
                            <div className="w-3/4 pl-2 flex gap-3">
                                <div className="flex-1">
                                    <input
                                        className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={formData.first_name}
                                        placeholder="Enter First name here"
                                        onChange={(e) => handleInputChange("first_name", e.target.value)}
                                    />
                                    {formError.first_name && (
                                        <p className="text-xs text-red-600 mt-1">{formError.first_name}</p>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <input
                                        className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={formData.last_name}
                                        placeholder="Enter Last name here"
                                        onChange={(e) => handleInputChange("last_name", e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* HR Email */}
                        <div className="flex mb-5">
                            <label className="w-1/4" />
                            <div className="w-3/4 pl-2">
                                <input
                                    type="email"
                                    className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    value={formData.email}
                                    placeholder="Enter Email address here"
                                    onChange={(e) => handleInputChange("email", e.target.value)}
                                />
                                {formError.email && (
                                    <p className="text-xs text-red-600 mt-1">{formError.email}</p>
                                )}
                            </div>
                        </div>

                        {/* HR Mobile */}
                        <div className="flex mb-5">
                            <label className="w-1/4" />
                            <div className="w-3/4 pl-2">
                                <CommonPhoneInput
                                    value={formData.mobile_number}
                                    onChange={(phone, country) => handlePhoneChange('mobile_number', phone, country)}
                                    onBlur={() => { }}
                                    placeholder="Enter Mobile number here"
                                    error={formError.mobile_number}
                                />
                            </div>
                        </div>

                        {/* Number of Employees */}
                        <div className="flex mb-5">
                            <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                                Number of employees*
                            </label>
                            <div className="w-3/4 pl-2">
                                <Select
                                    className="w-full h-9 select-options"
                                    placeholder="Select number of employees"
                                    options={numberOfEmployeesOptions}
                                    value={formData.max_employees}
                                    onChange={(selectedOption) => handleInputChange("max_employees", selectedOption)}
                                />
                                {formError.max_employees && (
                                    <p className="text-xs text-red-600 mt-1">{formError.max_employees}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </TabContent>

                {/* ── Tab 2: Address Details ── */}
                <TabContent value="tab2">
                    <div className="px-6">
                        {/* Country / State / City */}
                        <div className="flex mb-5">
                            <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                                Country/State/City
                            </label>
                            <div className="w-3/4 pl-2 flex gap-3">
                                <div className="flex-1">
                                    <Select
                                        className="w-full h-9 select-options"
                                        placeholder="Select Country"
                                        options={countryOptions}
                                        value={countryOptions.find(c => c.value === formData.country) || null}
                                        onChange={(selectedOption) => handleCountryChange(selectedOption?.value || 0)}
                                    />
                                </div>
                                <div className="flex-1">
                                    <Select
                                        className={`w-full h-9 select-options ${(!formData.country || stateOptions.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        placeholder="State"
                                        options={stateOptions}
                                        value={stateOptions.find(s => s.value === formData.state) || null}
                                        onChange={(selectedOption) => handleStateChange(selectedOption?.value || 0)}
                                        isDisabled={!formData.country || stateOptions.length === 0}
                                    />
                                </div>
                                <div className="flex-1">
                                    <Select
                                        className={`w-full h-9 select-options ${(!formData.state || cityOptions.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        placeholder="City"
                                        options={cityOptions}
                                        value={cityOptions.find(c => c.value === formData.city) || null}
                                        onChange={(selectedOption) => handleInputChange("city", selectedOption?.value || 0)}
                                        isDisabled={!formData.state || cityOptions.length === 0}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Pincode / Landline */}
                        <div className="flex mb-5">
                            <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                                Pincode/Landline
                            </label>
                            <div className="w-3/4 pl-2 flex gap-3">
                                <div className="flex-1">
                                    <input
                                        type="text" inputMode="numeric" pattern="[0-9]*"
                                        className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={formData.pincode}
                                        placeholder="Pincode"
                                        onChange={(e) => handleInputChange("pincode", e.target.value)}
                                        onKeyPress={handlePhoneKeyPress}
                                    />
                                </div>
                                <div className="flex-1">
                                    <CommonPhoneInput
                                        value={formData.phone_landline}
                                        onChange={(phone, country) => handlePhoneChange('phone_landline', phone, country)}
                                        onBlur={() => { }}
                                        placeholder="Phone Landline"
                                        error={formError.phone_landline}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Address */}
                        <div className="flex mb-5">
                            <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                                Address
                            </label>
                            <div className="w-3/4 pl-2">
                                <input
                                    className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-3"
                                    value={formData.address}
                                    placeholder="Line 1"
                                    onChange={(e) => handleInputChange("address", e.target.value)}
                                />
                                <input
                                    className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    value={formData.addressLine2}
                                    placeholder="Line 2"
                                    onChange={(e) => handleInputChange("addressLine2", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </TabContent>

                {/* ── Tab 3: Other Details ── */}
                <TabContent value="tab3">
                    <div className="px-6">
                        {/* Notes & Documents */}
                        {formData.notesAndDocuments.map((item, index) => (
                            <div key={index}>
                                <div className="flex mb-3">
                                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                                        {index === 0 ? "Notes & Documents" : ""}
                                    </label>
                                    <div className="w-3/4 pl-2">
                                        <textarea
                                            rows={4}
                                            className={`pl-3 pt-2 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${formError[`notes_${index}`] ? 'border-red-500' : 'border-gray-300'}`}
                                            value={item.notes}
                                            placeholder="Notes"
                                            onChange={(e) => handleNotesChange(index, e.target.value)}
                                        />
                                        {formError[`notes_${index}`] && (
                                            <p className="text-xs text-red-600 mt-1">{formError[`notes_${index}`]}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex mb-5">
                                    <label className="w-1/4" />
                                    <div className="w-3/4 pl-2 flex items-start gap-3">
                                        {item.documents && item.documents.length > 0 ? (
                                            <div className="flex-1 border border-gray-200 rounded-lg p-3">
                                                {item.documents.map((file, fileIndex) => {
                                                    const fileName = file instanceof File ? file.name : (typeof file === 'string' ? file.substring(file.lastIndexOf('/') + 1) : (file?.url ? file.url.substring(file.url.lastIndexOf('/') + 1) : 'Document'));
                                                    const fileSizeDisp = file instanceof File ? `${(file.size / 1024).toFixed(2)} KB - 100% uploaded` : `Uploaded File`;
                                                    const fileUrl = file instanceof File ? URL.createObjectURL(file) : (typeof file === 'string' ? file : (file?.url ? file.url : ''));

                                                    return (
                                                        <div key={fileIndex} className="flex items-center justify-between mb-2 last:mb-0">
                                                            <div className="flex items-center gap-3 overflow-hidden">
                                                                <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FF8E1C' }}>
                                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M11 2H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7l-5-5z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                        <path d="M11 2v5h5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                    </svg>
                                                                </div>
                                                                <div className="overflow-hidden">
                                                                    {fileUrl ? (
                                                                        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-gray-900 truncate hover:text-[#FF8E1C] hover:underline cursor-pointer block" title={fileName}>
                                                                            {fileName}
                                                                        </a>
                                                                    ) : (
                                                                        <p className="text-sm font-medium text-gray-900 truncate" title={fileName}>{fileName}</p>
                                                                    )}
                                                                    <p className="text-xs text-gray-500">{fileSizeDisp}</p>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() => removeFileFromNotesDocuments(index, fileIndex)}
                                                                className="h-9 w-9 flex items-center justify-center rounded-lg hover:opacity-80 flex-shrink-0"
                                                                style={{ backgroundColor: '#FF8E1C' }}
                                                            >
                                                                <CrossIcon />
                                                            </button>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        ) : (
                                            <Upload
                                                draggable
                                                showList={false}
                                                className="border-2 border-dashed border-gray-300 rounded-lg py-8 px-4 text-center hover:border-gray-400 transition-colors flex-1"
                                                onChange={(files) => handleNotesDocumentsChange(index, files)}
                                                multiple={true}
                                                maxFileSize={25 * 1024 * 1024}
                                                fileList={item.documents}
                                                accept=".jpg,.jpeg,.png,.pdf,.xls,.xlsx"
                                            >
                                                <div className="flex flex-col items-center">
                                                    <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <rect x="3" y="3" width="40" height="40" rx="20" fill="#EAECF0" />
                                                        <rect x="3" y="3" width="40" height="40" rx="20" stroke="#F9FAFB" strokeWidth="6" />
                                                        <path d="M19.666 26.3333L22.9993 23M22.9993 23L26.3327 26.3333M22.9993 23V30.5M29.666 26.9524C30.6839 26.1117 31.3327 24.8399 31.3327 23.4167C31.3327 20.8854 29.2807 18.8333 26.7493 18.8333C26.5673 18.8333 26.3969 18.7383 26.3044 18.5814C25.2177 16.7374 23.2114 15.5 20.916 15.5C17.4642 15.5 14.666 18.2982 14.666 21.75C14.666 23.4718 15.3622 25.0309 16.4885 26.1613" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    <span className="text-sm font-medium" style={{ color: '#FF8E1C' }}>Click to upload file</span>
                                                </div>
                                            </Upload>
                                        )}
                                        {formData.notesAndDocuments.length > 1 && (
                                            <button
                                                onClick={() => removeNotesAndDocumentsField(index)}
                                                className="h-9 w-9 flex items-center justify-center rounded-lg hover:opacity-80 flex-shrink-0"
                                                style={{ backgroundColor: '#FF8E1C' }}
                                            >
                                                <DeleteIcon />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Add Notes field */}
                        <div className="flex mb-5">
                            <label className="w-1/4" />
                            <div className="w-3/4 pl-2">
                                <div style={{ backgroundColor: '#FFF4E5', padding: '12px 24px', borderRadius: '8px' }}>
                                    <div className="flex justify-center">
                                        <button onClick={addNotesAndDocumentsField} className="text-sm font-medium flex items-center gap-2" style={{ color: '#FF8E1C' }}>
                                            Add another field
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Social Media Links */}
                        {formData.social_links.map((link, index) => (
                            <div key={index} className="flex mb-3">
                                <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                                    {index === 0 ? "Social Media Links" : ""}
                                </label>
                                <div className="w-3/4 pl-2 flex items-center gap-3">
                                    <input
                                        className="h-9 pl-3 flex-1 input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={link.url}
                                        placeholder={index === 0 ? "www.facebook.com" : "www.linkedin.com"}
                                        onChange={(e) => handleSocialMediaChange(index, e.target.value)}
                                    />
                                    {formData.social_links.length > 1 && (
                                        <button
                                            onClick={() => removeSocialMediaField(index)}
                                            className="h-9 w-9 flex items-center justify-center rounded-lg hover:opacity-80 flex-shrink-0"
                                            style={{ backgroundColor: '#FF8E1C' }}
                                        >
                                            <DeleteIcon />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Add Social Media field */}
                        <div className="flex mb-5">
                            <label className="w-1/4" />
                            <div className="w-3/4 pl-2">
                                <div style={{ backgroundColor: '#FFF4E5', padding: '12px 24px', borderRadius: '8px' }}>
                                    <div className="flex justify-center">
                                        <button onClick={addSocialMediaField} className="text-sm font-medium flex items-center gap-2" style={{ color: '#FF8E1C' }}>
                                            Add another field
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Assign Session — MANDATORY */}
                        <div className="flex mb-5">
                            <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                                Assign Session*
                            </label>
                            <div className="w-3/4 pl-2">
                                <Select
                                    className="w-full h-9 select-options"
                                    placeholder="Assign Session"
                                    options={sessionOptions}
                                    value={formData.session_id}
                                    onChange={(selectedOption) => handleInputChange("session_id", selectedOption)}
                                    isDisabled={isEdit}
                                />
                                {formError.session_id && (
                                    <p className="text-xs text-red-600 mt-1">{formError.session_id}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </TabContent>

                {/* ── Tab 4: Generate URL (Add mode only) ── */}
                {!isEdit && (
                    <TabContent value="tab4">
                        <div className="px-6">
                            <div className="flex mb-5">
                                <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                                    Generate URL
                                </label>
                                <div className="w-3/4 pl-2 flex flex-col gap-1">
                                    <div className="flex items-center gap-3">
                                        <input
                                            className="h-9 pl-3 flex-1 input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                            value={formData.generateUrl}
                                            placeholder="Generate URL"
                                            readOnly
                                        />
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={copyUrlToClipboard}
                                                className="h-9 w-9 flex items-center justify-center rounded-lg flex-shrink-0"
                                                style={{ backgroundColor: '#FF8E1C' }}
                                                disabled={!formData.generateUrl}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M16 1H4C2.89543 1 2 1.89543 2 3V15" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    <rect x="8" y="5" width="14" height="14" rx="2" stroke="white" strokeWidth="1.5" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    {copyMsg.text && (
                                        <div className={`text-sm ${copyMsg.type === 'success' ? 'text-green-600' : 'text-red-600'} pl-1`}>
                                            {copyMsg.text}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </TabContent>
                )}
            </Tabs>

            {/* Footer */}
            <div className="flex dailog-footer">
                <button
                    className="mr-1.5 whiteBtnShadow btn border border-[#FFF4E5] block w-full rounded-lg h-10 bg-white font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] hover:bg-[#FFF4E5] hover:border-[#FFF4E5] duration-100 secondary-btn"
                    onClick={handleDialogClose}
                    style={{ backgroundColor: '#FFF4E5' }}
                >
                    Close
                </button>

                {(tab === "tab1" || tab === "tab2" || tab === "tab3") && (
                    <BtnPrimary
                        disabled={isGeneratingUrl || buttonDisabled}
                        onClick={handleNext}
                        className="ml-1.5"
                        style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
                    >
                        {(isGeneratingUrl || buttonDisabled)
                            ? <ThreeDotLoader />
                            : (tab === 'tab3' ? (isEdit ? 'Update Company' : 'Add Company') : 'Next')
                        }
                    </BtnPrimary>
                )}
            </div>
        </Dialog>
    );
};

export default AddEditCompany;